import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSetRecoilState } from 'recoil';
import { ApiTreeLeaf, TreeNode } from '../../types';
import { TreeViewNode } from '../../components/TreeView/TreeView';
import { getTreeViewQuery } from '../../services/api';
import { Query } from '../../services/gql/types';
import * as atoms from '../../atoms';
import { TREE_CATEGORIES } from '../../constants/queryKeys';
import useGraphqlQuery from '../useGraphqlQuery';
import useSelectedEnv from '../useSelectedEnv';
import {
  collectLeafIds,
  excludeExposures,
  excludeTestsAndExposures,
  filterTree,
  formatTreeData,
  getAllSubNodes,
  getFolderNodeIds,
  includeModelsAndSources,
  includeModelsOnly,
  includeModelsSourcesTablesAndViews,
  selectSidebarData,
} from './utils';
import { BuildLeaf, TreeDataProps, TreeDataResult, TreeViews } from './types';

const buildApiLeaf: BuildLeaf = (leaf) => ({
  label: (leaf as ApiTreeLeaf).label,
  totals: (leaf as ApiTreeLeaf).totals,
});

const useSaaSTreeData = ({
  searchValue,
  treeType,
  treeViewBy,
  isAsyncTotalsEnabled = false,
  selectedSavedView,
}: TreeDataProps): TreeDataResult => {
  const setCurrentTreeData = useSetRecoilState(atoms.currentTreeData);
  const setCurrentTreeNodesById = useSetRecoilState(atoms.currentTreeNodesById);
  const envid = useSelectedEnv();
  const { pathname } = useLocation();

  const treeIncludesTotals = !isAsyncTotalsEnabled;

  const { data: apiTreeViewData, isLoading: isTreeViewDataLoading } =
    useGraphqlQuery<Pick<Query, 'treeView'>>({
      queryKey: [
        TREE_CATEGORIES,
        envid,
        treeType,
        treeViewBy,
        selectedSavedView?.filters,
        treeIncludesTotals,
      ],
      query: getTreeViewQuery,
      variables: {
        category: treeType?.toUpperCase(),
        view: treeViewBy?.toUpperCase(),
        filters: selectedSavedView?.filters || [],
        includeTotals: treeIncludesTotals,
      },
      enabled: !!treeType && !!treeViewBy,
    });

  // prefetch bi path tree view data to determine if there is bi data
  const { data: biPathTreeViewData } = useGraphqlQuery<Pick<Query, 'treeView'>>(
    {
      queryKey: [
        TREE_CATEGORIES,
        envid,
        'bi_assets',
        'paths',
        selectedSavedView?.filters,
        treeIncludesTotals,
      ],
      query: getTreeViewQuery,
      variables: {
        category: 'BI_ASSETS',
        view: 'PATHS',
        filters: selectedSavedView?.filters || [],
        includeTotals: treeIncludesTotals,
      },
      enabled: true,
    },
  );

  const treeViewData = apiTreeViewData?.treeView?.data;

  const variants = useMemo(
    () => ({
      noExposures: excludeExposures(treeViewData),
      noTestsNoExposures: excludeTestsAndExposures(treeViewData),
      noTestsNoEphemeral: filterTree(
        treeViewData,
        (leaf) =>
          leaf.resource_type !== 'test' &&
          (leaf as ApiTreeLeaf).is_ephemeral === false,
      ),
      modelsAndSources: includeModelsAndSources(treeViewData),
      modelsOnly: includeModelsOnly(treeViewData),
      modelsSourcesTablesAndViews:
        includeModelsSourcesTablesAndViews(treeViewData),
    }),
    [treeViewData],
  );

  const selectedSidebarData = useMemo(
    () => selectSidebarData(pathname, variants, treeViewData),
    [pathname, variants, treeViewData],
  );

  const treePathSeparator = treeViewBy === 'dwh' ? '.' : '/';

  const treeViews: TreeViews | undefined = useMemo(() => {
    if (!selectedSidebarData) return undefined;
    const sidebar = selectedSidebarData as TreeNode;
    return Object.keys(sidebar)
      .filter((key) => sidebar[key] != null)
      .map((key) =>
        formatTreeData(
          `folderNode_${key}`,
          key,
          sidebar[key],
          searchValue,
          buildApiLeaf,
          0,
          treePathSeparator,
          undefined,
        ),
      );
  }, [selectedSidebarData, searchValue, treePathSeparator]);

  const folderNodeIds = useMemo(
    () => treeViews?.map((tree) => getFolderNodeIds(tree)).flat() || [],
    [treeViews],
  );

  const treeViewsLeafIds = useMemo(
    () => (selectedSidebarData ? collectLeafIds(selectedSidebarData) : []),
    [selectedSidebarData],
  );

  useEffect(() => {
    setCurrentTreeData(treeViews);
    const treeNodesById: Record<string, TreeViewNode> = {};
    getAllSubNodes(treeViews || []).forEach((node) => {
      treeNodesById[node.id] = node;
    });
    setCurrentTreeNodesById(treeNodesById);
  }, [treeViews, setCurrentTreeData, setCurrentTreeNodesById]);

  return {
    treeViews,
    folderNodeIds,
    treeViewsLeafIds,
    isTreeDataLoading: isTreeViewDataLoading,
    hasBiData: Object.keys(biPathTreeViewData?.treeView?.data || {}).length > 0,
  };
};

export default useSaaSTreeData;
